import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  groupByWriter,
  summarizeWritingQuality,
} from "../../../../../shared/index";
import {
  ErrorBlock,
  FiltersBar,
  KpiCards,
  SortableHead,
  StatusBlock,
} from "./components";
import {
  carryFilters,
  formatScore,
  useEvaluatedArticles,
  useSort,
  useWritingQualityFilters,
} from "./hooks";

export default function WritingQualityPage() {
  const navigate = useNavigate();
  const { filters, setFilter } = useWritingQualityFilters();
  const query = useEvaluatedArticles(filters);
  const all = useMemo(() => query.data?.articles ?? [], [query.data]);

  const filtered = useMemo(
    () =>
      all.filter(
        (a) =>
          (!filters.writer || a.authorSlug === filters.writer) &&
          (!filters.action || a.recommendedAction === filters.action)
      ),
    [all, filters.writer, filters.action]
  );
  const summary = useMemo(() => summarizeWritingQuality(filtered), [filtered]);
  const writers = useMemo(() => groupByWriter(filtered), [filtered]);
  const { sorted, sort, toggle } = useSort(writers, "articles", "desc");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Writing Quality</h1>
        <p className="text-sm text-muted-foreground">
          AI-Like Writing and Editorial Quality scores across TipRanks writers.
        </p>
      </div>

      <FiltersBar filters={filters} setFilter={setFilter} articles={all} />

      {query.isError && <ErrorBlock error={query.error} fallback="Failed to load evaluations" />}
      {query.data?.truncated && (
        <div className="rounded-lg border bg-muted p-3 text-sm">
          Showing the newest {all.length} articles only. Narrow the date range to see them all.
        </div>
      )}

      <KpiCards summary={summary} />

      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">Writers {!query.isLoading && `(${writers.length})`}</h2>
        </div>
        {query.isLoading ? (
          <StatusBlock>
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </StatusBlock>
        ) : writers.length === 0 ? (
          <StatusBlock>No evaluated articles match these filters.</StatusBlock>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Writer" sortKey="authorName" sort={sort} onSort={toggle} />
                  <SortableHead label="Articles" sortKey="articles" sort={sort} onSort={toggle} className="text-right" />
                  <SortableHead label="Avg AI-Like Writing" sortKey="avgAiWriting" sort={sort} onSort={toggle} className="text-right" />
                  <SortableHead label="Avg Editorial Quality" sortKey="avgEditorialQuality" sort={sort} onSort={toggle} className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((w) => (
                  <TableRow
                    key={w.authorSlug}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/dashboard/writing-quality/writers/${encodeURIComponent(w.authorSlug)}?${carryFilters(filters)}`
                      )
                    }
                  >
                    <TableCell className="font-medium">{w.authorName}</TableCell>
                    <TableCell className="text-right tabular-nums">{w.articles}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatScore(w.avgAiWriting)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatScore(w.avgEditorialQuality)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
